/**
 * PipelineHistoryPanel — per-pipeline change-log drawer
 * (GET /pipeline-models/:key/history). Mirrors ModelHistoryPanel's layout.
 */

import React from 'react';
import { Box, Drawer, Typography, IconButton, Skeleton, Alert, Button, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatDistanceToNow } from 'date-fns';
import EmptyState from '../../common/EmptyState';
import { colors, typography } from '../../../theme/designTokens';
import { PipelineHistoryRow } from '../../../services/quillAdminService';

export interface PipelineHistoryPanelProps {
  open: boolean;
  pipelineKey: string | null;
  displayName: string | null;
  rows: PipelineHistoryRow[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onClose: () => void;
}

function modelLabel(provider: string | null | undefined, modelId: string | null | undefined): string {
  if (!modelId) return '(none)';
  return provider ? `${provider}/${modelId}` : modelId;
}

function safeTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : formatDistanceToNow(d, { addSuffix: true });
}

export const PipelineHistoryPanel: React.FC<PipelineHistoryPanelProps> = ({
  open,
  pipelineKey,
  displayName,
  rows,
  loading,
  error,
  onRetry,
  onClose,
}) => {
  const sorted = [...rows].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '90vw', sm: 440 }, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg }}>
            History
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, wordBreak: 'break-word' }}>
          {displayName ?? ''} {pipelineKey ? `· ${pipelineKey}` : ''}
        </Typography>

        {loading && (
          <Box>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 1 }} />
            ))}
          </Box>
        )}

        {!loading && error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {!loading && !error && sorted.length === 0 && (
          <EmptyState icon="no_data" title="No history yet" description="No changes have been recorded for this pipeline." />
        )}

        {!loading &&
          !error &&
          sorted.map((row, idx) => (
            <Box key={`${row.created_at}-${idx}`}>
              <Box sx={{ py: 1.5 }}>
                <Typography sx={{ fontFamily: typography.fonts.mono, fontSize: typography.sizes.sm }}>
                  {(row.old_model_id || row.old_provider) && (
                    <>
                      {modelLabel(row.old_provider, row.old_model_id)}{' '}
                      <Box component="span" sx={{ color: colors.deepTeal[500] }}>
                        →
                      </Box>{' '}
                    </>
                  )}
                  {modelLabel(row.provider, row.model_id)}
                </Typography>
                {(row.temperature != null || row.max_tokens != null) && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                    {row.temperature != null ? `temp ${row.temperature}` : ''}
                    {row.temperature != null && row.max_tokens != null ? ' · ' : ''}
                    {row.max_tokens != null ? `max_tokens ${row.max_tokens}` : ''}
                  </Typography>
                )}
                {row.reason && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {row.reason}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.5 }}>
                  {(row.actor_email || row.updated_by) ?? 'unknown'} • {safeTime(row.created_at)}
                </Typography>
              </Box>
              {idx < sorted.length - 1 && <Divider />}
            </Box>
          ))}
      </Box>
    </Drawer>
  );
};

export default PipelineHistoryPanel;
